import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface MetricsCardsProps {
  courses: any[];
  courseStatus: any;
  completionRate: number;
}

const MetricsCards = ({ courses, courseStatus, completionRate }: MetricsCardsProps) => {
  const { t } = useTranslation();
  
  const metrics = [
    {
      title: t("courses.totalCourses"),
      value: courses.length,
      color: "from-blue-500 to-blue-600",
      textColor: "text-blue-100",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
    },
    {
      title: t("courses.completed"),
      value: courseStatus?.completed || 0,
      color: "from-green-500 to-green-600",
      textColor: "text-green-100",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: t("courses.inProgress"),
      value: courseStatus?.inProgress || 0,
      color: "from-yellow-500 to-yellow-600",
      textColor: "text-yellow-100",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: t("courses.completionRate"),
      value: `${completionRate}%`,
      color: "from-purple-500 to-purple-600",
      textColor: "text-purple-100",
      icon: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 px-2 sm:px-0">
      {metrics.map((metric, index) => (
        <motion.div
          key={metric.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
        >
          <div className={`h-28 sm:h-32 bg-gradient-to-br ${metric.color} rounded-2xl p-4 sm:p-6 text-white shadow-lg`}>
            <div className="flex items-center justify-between h-full">
              <div className="min-w-0 flex-1 mr-2">
                <p className={`text-xs sm:text-sm font-medium ${metric.textColor} truncate`}>
                  {metric.title}
                </p>
                <p className="text-xl sm:text-2xl font-bold text-white truncate">
                  {metric.value}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                {metric.icon}
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default MetricsCards;
